import { generateKeyBetween } from "fractional-indexing";
import type { Item } from "../replicache/keys";

export type TreeNode = {
  item: Item;
  children: TreeNode[];
  depth: number;
};

const byKey = (a: { sortKey: string }, b: { sortKey: string }) =>
  a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0;

export function buildTree(items: Item[]): TreeNode[] {
  const byParent = new Map<string | null, Item[]>();
  for (const item of items) {
    const k = item.parentId ?? null;
    const list = byParent.get(k);
    if (list) list.push(item);
    else byParent.set(k, [item]);
  }
  for (const list of byParent.values()) list.sort(byKey);
  const build = (parentId: string | null, depth: number): TreeNode[] =>
    (byParent.get(parentId) ?? []).map((item) => ({
      item,
      depth,
      children: build(item.id, depth + 1),
    }));
  return build(null, 0);
}

// Depth-first list of visible nodes given current collapse state.  Index in
// this list is the source of truth for keyboard up/down nav.
export function flattenVisible(
  tree: TreeNode[],
  collapsed: Set<string>,
): TreeNode[] {
  const out: TreeNode[] = [];
  const walk = (nodes: TreeNode[]) => {
    for (const n of nodes) {
      out.push(n);
      if (n.children.length && !collapsed.has(n.item.id)) walk(n.children);
    }
  };
  walk(tree);
  return out;
}

export function findNode(tree: TreeNode[], id: string): TreeNode | null {
  for (const n of tree) {
    if (n.item.id === id) return n;
    const inChild = findNode(n.children, id);
    if (inChild) return inChild;
  }
  return null;
}

// Locate the immediate parent of `id` (if any) and its sibling array.  Used to
// figure out where a Tab/Shift-Tab move lands.
export function findContext(
  tree: TreeNode[],
  id: string,
): {
  parent: TreeNode | null;
  siblings: TreeNode[];
  index: number;
} | null {
  const search = (
    parent: TreeNode | null,
    siblings: TreeNode[],
  ): ReturnType<typeof findContext> => {
    const idx = siblings.findIndex((n) => n.item.id === id);
    if (idx >= 0) return { parent, siblings, index: idx };
    for (const n of siblings) {
      const sub = search(n, n.children);
      if (sub) return sub;
    }
    return null;
  };
  return search(null, tree);
}

// ---------------------------------------------------------------------------
// Sort-key planners.  Each returns the values that would change for a given
// edit; the caller invokes the relevant mutator.
// ---------------------------------------------------------------------------

/** Insert a new sibling immediately after `afterId` within its parent. */
export function planSiblingAfter(
  tree: TreeNode[],
  afterId: string,
): { parentId: string | null; sortKey: string } | null {
  const ctx = findContext(tree, afterId);
  if (!ctx) return null;
  const here = ctx.siblings[ctx.index]!;
  const next = ctx.siblings[ctx.index + 1] ?? null;
  return {
    parentId: ctx.parent?.item.id ?? null,
    sortKey: generateKeyBetween(here.item.sortKey, next?.item.sortKey ?? null),
  };
}

/** Insert a new child at the bottom of `parentId`'s children. */
export function planChildOf(
  tree: TreeNode[],
  parentId: string,
): { parentId: string; sortKey: string } | null {
  const parent = findNode(tree, parentId);
  if (!parent) return null;
  const lastChild = parent.children[parent.children.length - 1] ?? null;
  return {
    parentId,
    sortKey: generateKeyBetween(lastChild?.item.sortKey ?? null, null),
  };
}

/** Indent: become last child of previous sibling. */
export function planIndent(
  tree: TreeNode[],
  id: string,
): { parentId: string; sortKey: string } | null {
  const ctx = findContext(tree, id);
  if (!ctx || ctx.index === 0) return null;
  const prev = ctx.siblings[ctx.index - 1]!;
  const newLast = prev.children[prev.children.length - 1] ?? null;
  return {
    parentId: prev.item.id,
    sortKey: generateKeyBetween(newLast?.item.sortKey ?? null, null),
  };
}

export type DropPos = "before" | "inside" | "after";

/** Compute the new parent + sortKey for dropping `targetId`'s row at `pos`.
 *  `collapsed` is consulted because dropping "after" an expanded parent
 *  visually means "first child", not "next sibling".
 */
export function planDropAt(
  tree: TreeNode[],
  targetId: string,
  pos: DropPos,
  collapsed: ReadonlySet<string>,
): { parentId: string | null; sortKey: string } | null {
  const ctx = findContext(tree, targetId);
  if (!ctx) return null;
  const target = ctx.siblings[ctx.index]!;

  if (pos === "before") {
    const prev = ctx.siblings[ctx.index - 1] ?? null;
    return {
      parentId: ctx.parent?.item.id ?? null,
      sortKey: generateKeyBetween(prev?.item.sortKey ?? null, target.item.sortKey),
    };
  }

  if (pos === "after") {
    // After-an-expanded-parent visually = first-child-of-parent.
    if (target.children.length && !collapsed.has(target.item.id)) {
      const firstChild = target.children[0] ?? null;
      return {
        parentId: target.item.id,
        sortKey: generateKeyBetween(null, firstChild?.item.sortKey ?? null),
      };
    }
    const next = ctx.siblings[ctx.index + 1] ?? null;
    return {
      parentId: ctx.parent?.item.id ?? null,
      sortKey: generateKeyBetween(target.item.sortKey, next?.item.sortKey ?? null),
    };
  }

  // inside → become last child of target
  const lastChild = target.children[target.children.length - 1] ?? null;
  return {
    parentId: target.item.id,
    sortKey: generateKeyBetween(lastChild?.item.sortKey ?? null, null),
  };
}

// Roll-up stats shown next to header rows.  Walks every descendant of
// `node` (children, grandchildren, etc.), counting tasks of various
// flavours.  Headers in the tree never contribute to the totals
// themselves; only `type === "task"` items do.
//
// "Due soon" means "due today or within the next 2 days, not yet
// complete".  Overdue items are deliberately excluded — the date column
// on each row already renders overdue in red, and conflating "needs
// attention right now" with "is coming up" muddies both signals.
export type HeaderStats = {
  tasks: number;
  highPriority: number;
  dueSoon: number;
};

export function computeHeaderStats(node: TreeNode): HeaderStats {
  const today = todayISO();
  const horizon = addDaysISO(today, 2);
  let tasks = 0;
  let highPriority = 0;
  let dueSoon = 0;
  const walk = (n: TreeNode) => {
    for (const child of n.children) {
      const it = child.item;
      if (it.type === "task") {
        tasks++;
        if (!it.isComplete && it.priority === "high") highPriority++;
        if (!it.isComplete && it.dueDate) {
          const due = it.dueDate.slice(0, 10);
          if (due >= today && due <= horizon) dueSoon++;
        }
      }
      walk(child);
    }
  };
  walk(node);
  return { tasks, highPriority, dueSoon };
}

function todayISO(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function addDaysISO(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, (d ?? 1) + days);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

/** Set of {id} ∪ descendants — used to gate "can't drop onto myself/my children". */
export function descendantsOf(tree: TreeNode[], id: string): Set<string> {
  const out = new Set<string>([id]);
  const node = findNode(tree, id);
  if (!node) return out;
  const walk = (nodes: TreeNode[]) => {
    for (const n of nodes) {
      out.add(n.item.id);
      walk(n.children);
    }
  };
  walk(node.children);
  return out;
}

/** Outdent: become next sibling of current parent. No-op at the root. */
export function planOutdent(
  tree: TreeNode[],
  id: string,
): { parentId: string | null; sortKey: string } | null {
  const ctx = findContext(tree, id);
  if (!ctx || !ctx.parent) return null;
  const grand = findContext(tree, ctx.parent.item.id);
  if (!grand) {
    // Parent was a root node: become a root sibling immediately after parent.
    const idxOfParentInRoot = tree.findIndex(
      (n) => n.item.id === ctx.parent!.item.id,
    );
    const afterParent = tree[idxOfParentInRoot + 1] ?? null;
    return {
      parentId: null,
      sortKey: generateKeyBetween(
        ctx.parent.item.sortKey,
        afterParent?.item.sortKey ?? null,
      ),
    };
  }
  // Parent has a parent: target slot is between parent and its next sibling.
  const afterParent = grand.siblings[grand.index + 1] ?? null;
  return {
    parentId: grand.parent?.item.id ?? null,
    sortKey: generateKeyBetween(
      ctx.parent.item.sortKey,
      afterParent?.item.sortKey ?? null,
    ),
  };
}
